import React, {FC, useState, useEffect} from 'react'

import DiscordConnectModal from './DiscordConnectModal'
import DiscordManageModal from './DiscordManageModal'
import {discordApiService, DiscordIntegrationStatus} from '../services/discordApi'

interface DiscordIntegrationCardProps {}

// Só o administrador da empresa conecta. O RushMind atende por mensagem direta e menção, sem mapear canais.
const DISCORD_OAUTH_ERRORS: Record<string, string> = {
  guild_in_use: 'Esse servidor do Discord já está conectado a outra empresa.',
  not_admin: 'Apenas administradores podem conectar o Discord.',
  invalid_state: 'A conexão expirou. Clique em "Adicionar o RushMind ao Discord" para tentar de novo.',
}

const DiscordIntegrationCard: FC<DiscordIntegrationCardProps> = () => {
  const [showConnectModal, setShowConnectModal] = useState(false)
  const [showManageModal, setShowManageModal] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [integrationStatus, setIntegrationStatus] = useState<DiscordIntegrationStatus | null>(null)
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null)
  const [guildName, setGuildName] = useState<string | null>(null)
  const [mappedChannelsCount, setMappedChannelsCount] = useState<number>(0)

  // Carrega o status da integração ao montar o componente
  useEffect(() => {
    const loadStatus = async () => {
      setIsLoading(true)
      try {
        const status = await discordApiService.getStatus()
        setIntegrationStatus(status)
        setIsConnected(status.is_connected)
        
        if (status.is_connected && status.integration) {
          setGuildName(status.integration.guild_name)
          setMappedChannelsCount(status.integration.mapped_channels_count || 0)
        }
      } catch (error) {
        console.error('Erro ao carregar status da integração:', error)
        setMessage({type: 'error', text: 'Erro ao carregar status da integração'})
      } finally {
        setIsLoading(false)
      }
    }

    loadStatus()

    // Verifica se há parâmetros de erro na URL (vindos do callback OAuth)
    const urlParams = new URLSearchParams(window.location.search)
    const errorParam = urlParams.get('error')
    const messageParam = urlParams.get('message')
    
    if (errorParam === 'integration_exists' || messageParam?.includes('integração Discord ativa')) {
      setMessage({
        type: 'error',
        text: messageParam || 'Já existe uma integração Discord ativa para esta instância. Apenas uma integração é permitida por instância.'
      })
      // Remove os parâmetros da URL
      window.history.replaceState({}, '', window.location.pathname)
    } else if (errorParam && DISCORD_OAUTH_ERRORS[errorParam]) {
      // Erros do fluxo "Adicionar o RushMind ao Discord" (o backend só manda o código, o texto é nosso)
      setMessage({type: 'error', text: DISCORD_OAUTH_ERRORS[errorParam]})
      window.history.replaceState({}, '', window.location.pathname)
    } else if (errorParam && messageParam) {
      setMessage({
        type: 'error',
        text: messageParam
      })
      // Remove os parâmetros da URL
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  const handleConnect = () => {
    setShowConnectModal(true)
  }

  const handleManage = () => {
    setShowManageModal(true)
  }

  const handleDisconnect = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o Discord?')) {
      return
    }

    setIsLoading(true)
    try {
      await discordApiService.disconnect()
      setIsConnected(false)
      setIntegrationStatus(null)
      setGuildName(null)
      setMappedChannelsCount(0)
      setMessage({type: 'success', text: 'Discord desconectado com sucesso!'})
    } catch (error: any) {
      console.error('Erro ao desconectar:', error)
      setMessage({type: 'error', text: error.response?.data?.message || 'Erro ao desconectar Discord'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleConnectSuccess = async () => {
    // Recarrega o status após conexão bem-sucedida
    setIsLoading(true)
    try {
      const status = await discordApiService.getStatus()
      setIntegrationStatus(status)
      setIsConnected(status.is_connected)
      
      if (status.is_connected && status.integration) {
        setGuildName(status.integration.guild_name)
        setMappedChannelsCount(status.integration.mapped_channels_count || 0)
      }
      
      setMessage({type: 'success', text: 'Discord conectado com sucesso!'})
      setShowConnectModal(false)
    } catch (error) {
      console.error('Erro ao recarregar status:', error)
      setMessage({type: 'error', text: 'Erro ao verificar status da conexão'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleManageSuccess = async () => {
    // Recarrega o status após salvar configurações
    setIsLoading(true)
    try {
      const status = await discordApiService.getStatus()
      setIntegrationStatus(status)
      setIsConnected(status.is_connected)
      
      if (status.is_connected && status.integration) {
        setGuildName(status.integration.guild_name)
        setMappedChannelsCount(status.integration.mapped_channels_count || 0)
      }
      setShowManageModal(false)
      setMessage({type: 'success', text: 'Configurações salvas com sucesso!'})
    } catch (error) {
      console.error('Erro ao recarregar status:', error)
      setMessage({type: 'error', text: 'Erro ao recarregar informações da integração'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className='card h-100 w-100 d-flex flex-column'>
        <div className='card-header border-0 pt-5'>
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-50px me-5'>
              <div className='symbol-label bg-light-primary'>
                <i className='fab fa-discord fs-2x' style={{color: '#5d7a1f'}}></i>
              </div>
            </div>
            <div className='d-flex flex-column'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-4 mb-2'>Discord</span>
                <span className='text-muted mt-1 fw-semibold fs-7'>
                  Converse com o RushMind no Discord: consulte tarefas e projetos e crie tarefas sem abrir o TaskRush
                </span>
              </h3>
            </div>
          </div>
        </div>
        
        <div className='card-body pt-0 d-flex flex-column flex-grow-1'>
          <div className='mb-6 flex-grow-1'>
            <p className='text-muted fs-7 mb-4'>
              Conecte o Discord para criar tarefas diretamente a partir das conversas da sua equipe no TaskRush.
            </p>
            
            {/* Mensagens de sucesso/erro */}
            {message && (
              <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`}>
                <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
                {message.text}
                <button 
                  type='button' 
                  className='btn-close ms-auto' 
                  onClick={() => setMessage(null)}
                ></button>
              </div>
            )}
            
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex align-items-center justify-content-between'>
                <span className='text-muted fs-7'>Status:</span>
                <span className={`badge ${isConnected ? 'badge-light-success' : 'badge-light-warning'}`}>
                  {isConnected ? 'Conectado' : 'Não conectado'}
                </span>
              </div>
              
              {isConnected && guildName && (
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fs-7'>Servidor:</span>
                  <span className='text-muted fs-7'>{guildName}</span>
                </div>
              )}
              
              {isConnected && (
                <div className='d-flex align-items-center justify-content-between'>
                  <span className='text-muted fs-7'>Canais mapeados:</span>
                  <span className='text-muted fs-7'>{mappedChannelsCount}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className='mt-auto'>
            <div className='d-flex flex-column gap-2'>
              {!isConnected ? (
                <button
                  className='btn btn-sm btn-primary w-100'
                  onClick={handleConnect}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                      Conectando...
                    </>
                  ) : (
                    <>
                      <i className='fab fa-discord me-2'></i>
                      Adicionar o RushMind ao Discord
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button
                    className='btn btn-sm btn-primary w-100'
                    onClick={handleManage}
                    disabled={isLoading}
                  >
                    <i className='fas fa-cog me-2'></i>
                    Gerenciar integração
                  </button>
                  <button
                    className='btn btn-sm btn-light-danger w-100'
                    onClick={handleDisconnect}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                        Desconectando...
                      </>
                    ) : (
                      <>
                        <i className='fas fa-unlink me-2'></i>
                        Desconectar
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <DiscordConnectModal
        show={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        onSuccess={handleConnectSuccess}
      />
      
      <DiscordManageModal
        show={showManageModal}
        onClose={() => setShowManageModal(false)}
        onSuccess={handleManageSuccess}
        guildName={guildName || ''}
        mappedChannelsCount={mappedChannelsCount}
      />
    </>
  )
}

export default DiscordIntegrationCard

